import React, { useEffect, useState } from 'react';
import { FileText, Download, Loader2, Calendar, Users, HelpCircle, Image as ImageIcon, Trophy, Bot } from 'lucide-react';
import { adminDb } from '../lib/adminDb';

interface ReportData {
  users: number;
  premiumUsers: number;
  questions: number;
  questionsWithImage: number;
  featureFlagsActive: number;
  auditEntries: number;
  generatedAt: string;
}

export const ReportsPage: React.FC = () => {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    setLoading(true);
    try {
      const [usersRes, questionsRes, flagsRes, auditRes] = await Promise.all([
        adminDb.from('users').select('data'),
        adminDb.from('questions').select('id, image_placeholder'),
        adminDb.from('feature_flags').select('enabled'),
        adminDb.from('audit_logs').select('id'),
      ]);

      const users = usersRes.data || [];
      const questions = questionsRes.data || [];
      const flags = flagsRes.data || [];
      const audit = auditRes.data || [];

      setData({
        users: users.length,
        premiumUsers: users.filter(u => u.data?.isPremium).length,
        questions: questions.length,
        questionsWithImage: questions.filter(q => q.image_placeholder).length,
        featureFlagsActive: flags.filter(f => f.enabled).length,
        auditEntries: audit.length,
        generatedAt: new Date().toLocaleString('de-DE'),
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    if (!data) return;
    const report = `# Fahryo Admin Report
Generiert: ${data.generatedAt}

## Nutzer
- Gesamt: ${data.users}
- Premium: ${data.premiumUsers}
- Free: ${data.users - data.premiumUsers}
- Conversion Rate: ${data.users > 0 ? Math.round((data.premiumUsers / data.users) * 100) : 0}%

## Content
- Fragen: ${data.questions}
- Mit Bild: ${data.questionsWithImage} (${data.questions > 0 ? Math.round((data.questionsWithImage / data.questions) * 100) : 0}%)

## System
- Feature Flags aktiv: ${data.featureFlagsActive}
- Audit-Einträge: ${data.auditEntries}
`;
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fahryo_report_${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><FileText size={24} /> Reports</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      <div className="page-header">
        <h1><FileText size={24} /> Reports</h1>
        <div className="flex gap-2">
          <button className="btn btn-primary" onClick={handleExportReport}>
            <Download size={16} /> Report herunterladen
          </button>
          <button className="btn btn-secondary" onClick={generateReport}>
            Neu generieren
          </button>
        </div>
      </div>

      <div className="alert alert-info mb-6">
        <Calendar size={16} />
        <span>Letzter Report generiert: {data.generatedAt}</span>
      </div>

      {/* Report Cards */}
      <div className="grid-cols-3 mb-6">
        <div className="card" style={{ padding: 24 }}>
          <div className="flex items-center gap-3 mb-3">
            <Users size={20} color="var(--primary)" />
            <h3>Nutzer</h3>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between"><span className="text-sm">Gesamt</span><span style={{ fontWeight: 700 }}>{data.users}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm">Premium</span><span style={{ fontWeight: 700, color: 'var(--success)' }}>{data.premiumUsers}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm">Free</span><span style={{ fontWeight: 700 }}>{data.users - data.premiumUsers}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm">Conversion</span><span style={{ fontWeight: 700 }}>{data.users > 0 ? Math.round((data.premiumUsers / data.users) * 100) : 0}%</span></div>
          </div>
        </div>

        <div className="card" style={{ padding: 24 }}>
          <div className="flex items-center gap-3 mb-3">
            <HelpCircle size={20} color="var(--warning)" />
            <h3>Content</h3>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between"><span className="text-sm">Fragen</span><span style={{ fontWeight: 700 }}>{data.questions}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm">Mit Bild</span><span style={{ fontWeight: 700, color: 'var(--success)' }}>{data.questionsWithImage}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm">Ohne Bild</span><span style={{ fontWeight: 700 }}>{data.questions - data.questionsWithImage}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm">Bild-Rate</span><span style={{ fontWeight: 700 }}>{data.questions > 0 ? Math.round((data.questionsWithImage / data.questions) * 100) : 0}%</span></div>
          </div>
        </div>

        <div className="card" style={{ padding: 24 }}>
          <div className="flex items-center gap-3 mb-3">
            <Bot size={20} color="var(--info)" />
            <h3>System</h3>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between"><span className="text-sm">Feature Flags aktiv</span><span style={{ fontWeight: 700 }}>{data.featureFlagsActive}</span></div>
            <div className="flex items-center justify-between"><span className="text-sm">Audit-Einträge</span><span style={{ fontWeight: 700 }}>{data.auditEntries}</span></div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
